import { useCallback } from 'react';
import { BlockerFunction, useBlocker } from 'react-router';

interface UseNavigationBlockerProps {
  savingInProgress: boolean;
  hasUnsavedChanges: boolean;
  hasExceededUsageLimit: boolean;
  onConfirm: () => Promise<string | null>;
}

export const useNavigationBlocker = ({
  savingInProgress,
  hasUnsavedChanges,
  hasExceededUsageLimit,
  onConfirm,
}: UseNavigationBlockerProps) => {
  const shouldBlock = useCallback<BlockerFunction>(
    () => !savingInProgress && hasUnsavedChanges && !hasExceededUsageLimit,
    [savingInProgress, hasUnsavedChanges, hasExceededUsageLimit]
  );

  const blocker = useBlocker(shouldBlock);

  const confirmAndProceed = useCallback(async () => {
    const succeed = await onConfirm();

    if (succeed) {
      blocker.proceed?.();
    } else {
      blocker.reset?.();
    }
  }, [onConfirm, blocker]);

  const proceedWithoutSaving = useCallback(() => {
    blocker.proceed?.();
  }, [blocker]);

  const resetBlocker = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  const isBlocked = blocker.state === 'blocked';

  return {
    isBlocked,
    confirmAndProceed,
    proceedWithoutSaving,
    resetBlocker,
  };
};
